import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useData } from '../contexts/DataContext';
import firebaseService from '../services/firebaseService';
import { findRestroomByShortId } from '../utils/shortId';
import { Newspaper, MapPin, Star, MessageSquare, Heart, ArrowLeft, ArrowDown, Loader2, User } from 'lucide-react';
import { getRelativeTime, getLikeMilestone, formatRating } from '../utils/timeUtils';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

const Container = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  left: 0;
  top: 0;
  background: #614F48;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  transition: background 0.2s;
  
  &:hover {
    background: #4a3c37;
  }
  
  @media (max-width: 768px) {
    position: static;
    margin-bottom: 15px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #231F20;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  font-size: 0.95rem;
`;

const Branding = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 18px;
  color: #231F20;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const BrandingLogo = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 6px;
`;

const FeedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeedItem = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 14px;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
`;

const ItemIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  
  ${props => props.type === 'new_restroom' && `
    background: #614F48;
    color: white;
  `}
  
  ${props => props.type === 'new_review' && `
    background: #614F48;
    color: white;
  `}
  
  ${props => props.type === 'review_milestone' && `
    background: #ff6445;
    color: white;
  `}
  
  ${props => props.type === 'new_user' && `
    background: #faaf40;
    color: #231F20;
  `}
`;

const ItemContent = styled.div`
  flex: 1;
`;

const ItemTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #231F20;
  margin-bottom: 6px;
  text-align: left;
`;

const ItemMeta = styled.div`
  font-size: 0.85rem;
  color: #95a5a6;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 8px;
  text-align: left;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  text-align: left;
`;

const ItemBody = styled.div`
  margin-top: 8px;
  margin-left: 52px;
  text-align: left;
`;

const ReviewSnippet = styled.div`
  font-size: 0.9rem;
  line-height: 1.4;
  color: #95a5a6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: #231F20;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
  margin-top: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  color: #7f8c8d;
  font-size: 0.9rem;
`;

const GreyPoop = styled.span`
  opacity: 0.3;
  filter: grayscale(100%);
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 15px 20px;
  margin: 20px 0;
  background: #ff6445;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #ff6445;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(236, 74, 31, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.3;
`;

const DailyStatsContainer = styled.div`
  background: #faaf40;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px rgba(250, 175, 64, 0.3);
`;

const DailyStatsTitle = styled.div`
  color: #231F20;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 8px;
  text-align: center;
`;

const DailyStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const DailyStatCard = styled.div`
  background: #614F48;
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  border: 1px solid rgba(35, 31, 32, 0.2);
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(97, 79, 72, 0.3);
  }
`;

const DailyStatIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 8px;
`;

const DailyStatValue = styled.div`
  color: white;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 4px;
`;

const DailyStatLabel = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.7rem;
  font-weight: 500;
`;

const TotalStatsContainer = styled.div`
  background: #faaf40;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px rgba(250, 175, 64, 0.3);
  text-align: center;
`;

const TotalStatsTitle = styled.div`
  color: #231F20;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 6px;
`;

const TotalStatsValue = styled.div`
  color: #231F20;
  font-size: 1.6rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

function DailyFlush() {
  const navigate = useNavigate();
  const { restrooms } = useData();
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const feedLengthRef = useRef(0);
  const loadingRef = useRef(false);
  
  // 24-hour activity summary
  const [dailyStats, setDailyStats] = useState({
    restroomsAdded: 0,
    reviewsAdded: 0,
    newUsers: 0
  });
  
  // All-time stats
  const [totalStats, setTotalStats] = useState({
    totalRestrooms: 0
  });
  
  // Pull-to-refresh state
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadFeedItems = useCallback(async (reset = false) => {
    if (loadingRef.current) return;
    
    if (reset) {
      feedLengthRef.current = 0;
    }
    
    setLoading(true);
    loadingRef.current = true;
    
    try {
      const functions = getFunctions();
      
      // Connect to emulator if on localhost
      // if (window.location.hostname === 'localhost') {
      //   try {
      //     connectFunctionsEmulator(functions, '127.0.0.1', 5001);
      //   } catch (e) {
      //     // Ignore "already connected" errors in React Strict Mode
      //   }
      // }
      
      const getDailyFlushData = httpsCallable(functions, 'dailyflushfunction');
      
      // Pass the database_id_hash from environment variables
      const databaseIdHash = process.env.REACT_APP_DATABASE_ID_HASH;
      
      const result = await getDailyFlushData({ database_id_hash: databaseIdHash });
      const { activities, stats } = result.data;
      
        setFeedItems(activities || []);
      setDailyStats({
        restroomsAdded: stats.restroomsAdded,
        reviewsAdded: stats.reviewsAdded,
        newUsers: stats.newUsers
      });
      setTotalStats({
        totalRestrooms: stats.totalRestrooms
      });
      
      // Since we are fetching everything at once from the cloud function (limited to 50),
      // we can just set hasMore to false for now unless we implement pagination in the cloud function.
      // The current implementation returns a fixed list.
      setHasMore(false);
      
    } catch (error) {
      console.error("Error fetching daily flush data:", error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // Load initial feed
  useEffect(() => {
    loadFeedItems();
  }, [loadFeedItems]);

  // Pull-to-refresh handlers
  const handleTouchStart = (e) => {
    if (window.scrollY === 0) {
      setStartY(e.touches[0].clientY);
      setIsPulling(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isPulling) return;
    
    const currentY = e.touches[0].clientY;
    const distance = currentY - startY;
    
    if (distance > 0) {
      e.preventDefault();
      setPullDistance(Math.min(distance, 120)); // Max pull distance of 120px
    }
  };

  const handleTouchEnd = async () => {
    if (!isPulling) return;
    
    if (pullDistance > 80) { // Threshold for refresh
      setIsRefreshing(true);
      await loadFeedItems(true); // Refresh the feed
      setIsRefreshing(false);
    }
    
    // Reset pull state
    setIsPulling(false);
    setPullDistance(0);
    setStartY(0);
  };

  // Load More button handler
  const handleLoadMore = () => {
    loadFeedItems(false); // Load more items
  };

  const handleItemClick = async (item) => {
    if (item.type === 'new_restroom' || item.type === 'review_milestone') {
      const restroomId = item.id;
      // Navigate to restroom on map - use ID field for proper matching
      let restroom = restrooms.find(r => r.id === restroomId) || findRestroomByShortId(restroomId, restrooms);
      
      if (!restroom) {
        // If not found in local context, fetch from firebase to get location data
        try {
          restroom = await firebaseService.getRestroomById(restroomId);
        } catch (error) {
          console.error('Error fetching restroom details:', error);
        }
      }

      if (restroom) {
        navigate('/app', { state: { selectedRestroom: restroom, zoom: 17 } });
      } else {
        // Fallback to URL if we can't find/fetch the object
        navigate(`/app/r/${restroomId}`);
      }
    } else if (item.type === 'new_review') {
      const restroomId = item.restroomId;
      // Navigate to restroom details
      let restroom = restrooms.find(r => r.id === restroomId) || findRestroomByShortId(restroomId, restrooms);
      
      if (!restroom) {
        try {
          restroom = await firebaseService.getRestroomById(restroomId);
        } catch (error) {
          console.error('Error fetching restroom details:', error);
        }
      }

      if (restroom) {
        navigate('/app', { state: { selectedRestroom: restroom, zoom: 17 } });
      } else {
        navigate(`/app/r/${restroomId}`);
      }
    }
  };

  const renderFeedItem = (item, index) => {
    const handleProfileClick = (event, username) => {
      event.stopPropagation();
      if (!username) return;
      navigate(`/app/profile/${encodeURIComponent(username)}`);
    };

    const getTPDisplay = (feedItem) => {
      if (Number.isFinite(Number(feedItem?.tp))) {
        return `${Number(feedItem.tp)} TP`;
      }

      if (Number.isFinite(Number(feedItem?.totalTP))) {
        return `${Number(feedItem.totalTP)} TP`;
      }

      if (feedItem?.type === 'new_restroom') {
        return '25 TP';
      }

      if (feedItem?.type === 'new_review') {
        return '10 TP';
      }

      return '0 TP';
    };

    return (
      <FeedItem 
        key={item.id}
        onClick={() => handleItemClick(item)}
      >
        <ItemHeader>
          <ItemIcon type={item.type}>
            {item.type === 'new_restroom' && '🚽'}
            {item.type === 'new_review' && <MessageSquare size={20} />}
            {item.type === 'review_milestone' && <Heart size={20} />}
            {item.type === 'new_user' && <User size={20} />}
          </ItemIcon>
          <ItemContent>
            <ItemTitle>{item.type === 'new_user' ? 'New User' : (item.name || item.restroomName)}</ItemTitle>
            <ItemMeta>
              {item.type === 'new_restroom' && (
                <>
                  <MetaItem>
                    Added by{' '}
                    <strong>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(event) => handleProfileClick(event, item.username)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            handleProfileClick(event, item.username);
                          }
                        }}
                        style={{ textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {item.username || 'anonymous'}
                      </span>
                    </strong>
                    {' '}• {getTPDisplay(item)} • {getRelativeTime(item.createdAt)}
                  </MetaItem>
                  {item.comment && (
                    <MetaItem>
                      {formatRating(item.rating)}
                    </MetaItem>
                  )}
                </>
              )}
              {item.type === 'new_review' && (
                <>
                  <MetaItem>
                    Review by{' '}
                    <strong>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(event) => handleProfileClick(event, item.username)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            handleProfileClick(event, item.username);
                          }
                        }}
                        style={{ textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {item.username || 'anonymous'}
                      </span>
                    </strong>
                    {' '}• {getTPDisplay(item)} • {getRelativeTime(item.createdAt)}
                  </MetaItem>
                  <MetaItem>
                    {formatRating(item.rating)}
                  </MetaItem>
                </>
              )}
              {item.type === 'review_milestone' && (
                <MetaItem>
                  👍 {item.likes} likes! • {getRelativeTime(item.createdAt)}
                </MetaItem>
              )}
              {item.type === 'new_user' && (
                <MetaItem>
                  Welcome{' '}
                  <strong>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(event) => handleProfileClick(event, item.username)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          handleProfileClick(event, item.username);
                        }
                      }}
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {item.username}
                    </span>
                  </strong>
                  ! • {getRelativeTime(item.createdAt)}
                </MetaItem>
              )}
            </ItemMeta>
          </ItemContent>
        </ItemHeader>
        
        {/* Show original review comment for new restrooms */}
        {item.type === 'new_restroom' && item.comment && (
          <ItemBody>
            <ReviewSnippet expanded={false}>
              {item.comment}
            </ReviewSnippet>
          </ItemBody>
        )}
        
        {/* Show review comment for new reviews */}
        {item.type === 'new_review' && item.comment && (
          <ItemBody>
            <ReviewSnippet expanded={false}>
              {item.comment}
            </ReviewSnippet>
          </ItemBody>
        )}
        
        {item.type === 'review_milestone' && (
          <ItemBody>
            🎉 This restroom's review just hit {item.milestone} likes!
          </ItemBody>
        )}
      </FeedItem>
    );
  };

  return (
    <Container 
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      {isPulling && (
        <div style={{
          position: 'fixed',
          top: `${Math.max(pullDistance - 60, -60)}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 20px',
          background: 'white',
          borderRadius: '25px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000,
          transition: 'top 0.2s ease'
        }}>
          {isRefreshing ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <ArrowDown size={20} style={{ 
              transform: pullDistance > 80 ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }} />
          )}
          <span style={{ fontSize: '14px', color: '#7f8c8d' }}>
            {isRefreshing ? 'Refreshing...' : pullDistance > 80 ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      )}
      
      <Header>
        <BackButton onClick={() => navigate('/')}>
          <ArrowLeft size={18} />
          Back to Map
        </BackButton>
        <Branding>
          <BrandingLogo src="/app-icon.png" alt="The Crapper Mapper" />
          Crapper Mapper
        </Branding>
        <Title>
          <Newspaper size={32} />
          The Daily Flush
        </Title>
        <Subtitle>Latest restroom activity, reviews, and discoveries</Subtitle>
      </Header>

      {/* Total Restrooms Reviewed */}
      <TotalStatsContainer>
        <TotalStatsTitle>TOTAL RESTROOMS REVIEWED</TotalStatsTitle>
        <TotalStatsValue>{totalStats.totalRestrooms.toLocaleString()}</TotalStatsValue>
      </TotalStatsContainer>

      {/* 24-Hour Activity Summary */}
      <DailyStatsContainer>
        <DailyStatsTitle>Last 24 Hours Activity</DailyStatsTitle>
        <DailyStatsGrid>
          <DailyStatCard>
            <DailyStatValue>{dailyStats.restroomsAdded}</DailyStatValue>
            <DailyStatLabel>Restrooms</DailyStatLabel>
          </DailyStatCard>
          
          <DailyStatCard>
            <DailyStatValue>{dailyStats.reviewsAdded}</DailyStatValue>
            <DailyStatLabel>Reviews</DailyStatLabel>
          </DailyStatCard>
          
          <DailyStatCard>
            <DailyStatValue>{dailyStats.newUsers}</DailyStatValue>
            <DailyStatLabel>New Users</DailyStatLabel>
          </DailyStatCard>
        </DailyStatsGrid>
      </DailyStatsContainer>

      {feedItems.length === 0 && !loading ? (
        <EmptyState>
          <EmptyIcon>🚽</EmptyIcon>
          <h3>No Activity Yet!</h3>
          <p>Be the first to add a restroom or review and get the flush flowing!</p>
        </EmptyState>
      ) : (
        <>
          <FeedContainer>
            {feedItems.map(renderFeedItem)}
          </FeedContainer>
          
          {/* Load More Button */}
          {hasMore && !loading && (
            <LoadMoreButton onClick={handleLoadMore}>
              Load More Activity
            </LoadMoreButton>
          )}
          
          {/* Loading indicator for Load More */}
          {loading && feedItems.length > 0 && (
            <LoadingIndicator>
              <Loader2 size={24} className="animate-spin" />
              Loading more activity...
            </LoadingIndicator>
          )}
        </>
      )}

      {loading && feedItems.length === 0 && (
        <LoadingIndicator>
          <Loader2 size={24} className="animate-spin" />
          Loading activity...
        </LoadingIndicator>
      )}
    </Container>
  );
}

export default DailyFlush;
